"use client"

import { usePathname } from "next/navigation"
import { NotFoundBeacon } from "@/components/analytics/not-found-beacon"
import { buttonVariants } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { localizedPath } from "@/lib/localized-route-slugs"

const NL_ENABLED = process.env.NL_ENABLED !== "false"
const FR_ENABLED = process.env.FR_ENABLED !== "false"

const VALID_COMBOS: Record<string, string[]> = {
  ch: ["en", "de", ...(FR_ENABLED ? ["fr"] : [])],
  ...(NL_ENABLED ? { nl: ["nl", "en"] } : {}),
}

type Copy = {
  label: string
  title: string
  description: string
  goHome: string
  contactUs: string
  popularPages: string
  howItWorks: string
  pricing: string
  locations: string
  contact: string
}

const translations: Record<string, Copy> = {
  en: {
    label: "404",
    title: "Page not found",
    description:
      "The page you’re looking for doesn’t exist or has been moved. Here are some helpful links to get you back on track.",
    goHome: "Go to homepage",
    contactUs: "Contact us",
    popularPages: "Popular pages",
    howItWorks: "How it works",
    pricing: "Pricing",
    locations: "Locations",
    contact: "Contact us",
  },
  de: {
    label: "404",
    title: "Seite nicht gefunden",
    description:
      "Die Seite, die Sie suchen, existiert nicht oder wurde verschoben. Hier finden Sie hilfreiche Links.",
    goHome: "Zur Startseite",
    contactUs: "Kontakt",
    popularPages: "Beliebte Seiten",
    howItWorks: "So funktioniert’s",
    pricing: "Preise",
    locations: "Standorte",
    contact: "Kontakt",
  },
  fr: {
    label: "404",
    title: "Page introuvable",
    description:
      "La page que vous recherchez n’existe pas ou a été déplacée. Voici quelques liens utiles.",
    goHome: "Retour à l’accueil",
    contactUs: "Nous contacter",
    popularPages: "Pages populaires",
    howItWorks: "Comment ça marche",
    pricing: "Tarifs",
    locations: "Nos centres",
    contact: "Nous contacter",
  },
  nl: {
    label: "404",
    title: "Pagina niet gevonden",
    description:
      "De pagina die je zoekt bestaat niet of is verplaatst. Hier zijn enkele handige links om je op weg te helpen.",
    goHome: "Naar de homepage",
    contactUs: "Neem contact op",
    popularPages: "Populaire pagina’s",
    howItWorks: "Hoe het werkt",
    pricing: "Prijzen",
    locations: "Locaties",
    contact: "Neem contact op",
  },
}

function detectLocale(pathname: string): {
  country: string
  locale: string
} {
  const segments = pathname.split("/").filter(Boolean)
  const country = segments[0] ?? ""
  const locale = segments[1] ?? ""

  if (VALID_COMBOS[country]?.includes(locale)) {
    return { country, locale }
  }
  return { country: "ch", locale: "en" }
}

export default function NotFoundClient() {
  const pathname = usePathname()
  const { country, locale } = detectLocale(pathname)
  const t = translations[locale] ?? translations.en
  const base = `/${country}/${locale}`

  const suggestedLinks = [
    { label: t.howItWorks, href: `${base}/how-it-works` },
    { label: t.pricing, href: `${base}/pricing` },
    {
      label: t.locations,
      href: `${base}${localizedPath("/locations", locale)}`,
    },
    { label: t.contact, href: `${base}/contact` },
  ]

  return (
    <div className="flex min-h-screen flex-1 items-center justify-center px-6 py-24 md:py-32">
      <NotFoundBeacon surface="root" />
      <div className="mx-auto max-w-xl text-center">
        <p className="text-sm font-medium tracking-wider text-neutral-400 uppercase">
          {t.label}
        </p>

        <h1 className="text-aubergine-900 mt-4 text-3xl font-normal sm:text-5xl md:text-6xl">
          {t.title}
        </h1>

        <p className="mx-auto mt-4 max-w-md text-base text-neutral-500">
          {t.description}
        </p>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <a href={base} className={cn(buttonVariants({ variant: "primary" }))}>
            {t.goHome}
          </a>
          <a
            href={`${base}/contact`}
            className={cn(buttonVariants({ variant: "outline" }))}
          >
            {t.contactUs}
          </a>
        </div>

        <nav className="mt-12" aria-label={t.popularPages}>
          <p className="mb-4 text-xs font-medium tracking-wider text-neutral-400 uppercase">
            {t.popularPages}
          </p>
          <ul className="flex flex-wrap items-center justify-center gap-2">
            {suggestedLinks.map((link) => (
              <li key={link.href}>
                <a
                  href={link.href}
                  className="text-aubergine-900 inline-block rounded-full bg-white px-4 py-2 text-sm transition-colors hover:bg-neutral-100"
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </div>
  )
}
